import type { KyInstance } from 'ky';

import { ensureError, formatString, omit } from '@enterprise/core';
import type { OperationResultWithData, PagedData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { PagedMailingListByMailingQuery } from './types/paged-mailing-list-query-by-mailing.type';
import type { PagedMailingList } from './types/paged-mailing-list.type';

/**
 * Retrieves a paged list of mailing lists filtered by mailing criteria.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param query - The query parameters for filtering and paginating mailing lists.
 *   - `isClosed` (optional): Whether to include closed mailing lists. Defaults to `false`.
 *   - Additional search parameters as defined in `PagedMailingListByMailingQuery`, excluding `filter` and `sort`.
 * @param isExternal - Whether to use the external mailings endpoint. Defaults to `false`.
 * @returns A promise that resolves to an `OperationResultWithData` containing paged mailing list data.
 * @throws Will throw an error if the HTTP request fails.
 */
export const getPagedMailingListsByMailing = async (
  instance: KyInstance,
  query: PagedMailingListByMailingQuery,
  isExternal = false
) => {
  try {
    const { isClosed = false, mailingId, ...searchParams } = omit(query, 'filter', 'sort');

    return await instance
      .get<OperationResultWithData<PagedData<PagedMailingList>>>(
        formatString(
          isExternal ? Uri.mailing.getPagedExternalMailingList : Uri.mailing.getPagedMailingLists,
          mailingId.toString()
        ),
        {
          searchParams: {
            ...searchParams,
            isClosed,
          },
        }
      )
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
